import { generateClient } from "aws-amplify/api";
import { createPurchase, updatePurchase } from "../graphql/mutations";
import {
  getPurchase,
  getPurchaseByProductId,
  getPurchaseByQuestionnaireResponseId,
  getPurchaseByStatus,
  getPurchaseBySub,
  getReportInfo,
} from "../graphql/queries";
import { v4 } from "uuid";
import { PurchasedStatus, surveyIds } from "./constants";

const client = generateClient();

const purchaseService = {
  getPurchaseByProductId: async (productId, sub) => {
    const response = await client.graphql({
      query: getPurchaseByProductId,
      variables: {
        productId,
      },
      authMode: "userPool",
    });
    return response.data?.getPurchaseByProductId?.items || [];
  },
  getPurchaseByStatus: async (status) => {
    const response = await client.graphql({
      query: getPurchaseByStatus,
      variables: {
        status,
      },
      authMode: "userPool",
    });
    return response.data?.getPurchaseByStatus?.items || [];
  },

  getPurchaseBySub: async (sub) => {
    const response = await client.graphql({
      query: getPurchaseBySub,
      variables: {
        sub,
      },
      authMode: "userPool",
    });
    return response.data?.getPurchaseBySub?.items || [];
  },

  getPurchaseByQuestionnaireResponseId: async (qrId) => {
    const response = await client.graphql({
      query: getPurchaseByQuestionnaireResponseId,
      variables: {
        questionnaireResponseId: qrId,
      },
      authMode: "userPool",
    });
    return response.data?.getPurchaseByQuestionnaireResponseId?.items || [];
  },

  // Return type - [ currentPlan, usedReports[], extraReports[], extraReportsUsed[] ]
  // Index 0: current Plan
  // Index 1: Array of usedReports for the plan
  // Index 2: Array of extraReports for the plan
  getCurrentPlanAndUsedReportsBySub: async (sub) => {
    const response = await client.graphql({
      query: getPurchaseBySub,
      variables: {
        sub,
      },
      authMode: "userPool",
    });

    const purchases = response.data?.getPurchaseBySub?.items;
    if (purchases?.length) {
      const sortedPurchases = purchases
        .filter((p) =>
          ["PLAN", surveyIds.SMSF001, surveyIds.SMSF001E].includes(
            p.productType
          )
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      const currentPlanIndex = sortedPurchases?.findIndex(
        (p) => p.productType === "PLAN"
      );
      // If there is no plan - extra reports data cannot be seen:
      if (currentPlanIndex === -1) {
        return [];
      }
      const currentPlan = sortedPurchases && sortedPurchases[currentPlanIndex];

      const { usedReports, extraReports } = sortedPurchases?.reduce(
        (result, purchase, pIndex) => {
          switch (purchase.productType) {
            case surveyIds.SMSF001:
              if (
                pIndex < currentPlanIndex &&
                purchase.status === PurchasedStatus.purchased
              ) {
                result.usedReports.push(purchase);
              }
              break;
            case surveyIds.SMSF001E:
              if (purchase.status === PurchasedStatus.purchased) {
                result.extraReports.push(purchase);
              }
              break;
          }
          return result;
        },
        {
          usedReports: [],
          extraReports: [],
        }
      );

      // Type: [currentPlan, usedReports[], extraReports[]]
      return [currentPlan, usedReports, extraReports];
    }

    return [];
  },

  getPurchaseById: async (puchaseId) => {
    const response = await client.graphql({
      query: getPurchase,
      variables: {
        id: puchaseId,
      },
      authMode: "userPool",
    });
    return response?.data?.getPurchase || undefined;
  },
  addPurchase: async (items) => {
    return await client.graphql({
      query: createPurchase,
      variables: {
        input: {
          id: v4(),
          productType: items.productType,
          productId: items.productId,
          sub: items.sub,
          activationDate: items.activationDate,
          expiryDate: items.expiryDate,
          questionnaireResponseId: items.questionnaireResponseId,
          edits: 0,
          status: items.status,
        },
      },
      authMode: "userPool",
    });
  },
  updatePurchaseQRId: async (
    purchaseId,
    questionnaireResponseId,
    status,
    edits
  ) => {
    return client.graphql({
      query: updatePurchase,
      variables: {
        input: {
          id: purchaseId,
          questionnaireResponseId,
          status,
          edits,
        },
      },
      authMode: "userPool",
    });
  },
  updatePurchaseStatus: async (purchaseId, status) => {
    const response = await client.graphql({
      query: updatePurchase,
      variables: {
        input: {
          id: purchaseId,
          status,
        },
      },
      authMode: "userPool",
    });

    return response?.data?.updatePurchase;
  },
  getReportInfo: async (data) => {
    const response = await client.graphql({
      query: getReportInfo,
      variables: {
        data,
      },
      authMode: "userPool",
    });
    return response.data?.getReportInfo;
  },
};

export default purchaseService;
